type EventHasProps<T> = [T] extends [string]
  ? {
      [K in T as `hasOn${Capitalize<K>}`]: ComputedRef<boolean>
    }
  : T extends (event: infer U, ...args: unknown[]) => unknown
    ? U extends string
      ? {
          [K in U as `hasOn${Capitalize<K>}`]: ComputedRef<boolean>
        }
      : never
    : never

export function useHasEvents<
  T extends ((...args: any) => unknown) | string,
>(): EventHasProps<T> {
  const instance = getCurrentInstance()

  if (!instance) {
    throw new Error('useHasEvents must be called within a setup function')
  }

  // Use Proxy to create properties on-demand
  return new Proxy({} as EventHasProps<T>, {
    get(target: any, prop: string | symbol) {
      if (typeof prop !== 'string' || !prop.startsWith('hasOn')) {
        return undefined
      }

      // Cache the computed ref so it's not recreated on each access
      if (!target[prop]) {
        // Convert hasOnClose -> onClose, hasOnCustomEvent -> onCustomEvent
        const eventPropName = `on${prop.slice(5)}` // Remove 'hasOn' and add 'on'

        target[prop] = computed(() => !!instance?.vnode.props?.[eventPropName])
      }

      return target[prop]
    },
  })
}
